import { ChangeDetectionStrategy, Component, computed, inject, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { FikoYTKokBilesenComponent } from '@/src/service/kok-bilesen/kok-bilesen.component';
import { HmiLookupService, ILookupMusteri, ILookupTedarikci } from '../../../services/hmi-lookup.service';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';

@Component({
  selector: 'teklif-edit',
  standalone: true,
  imports: [FormsModule, NzInputModule, NzSelectModule, NzDatePickerModule, NzInputNumberModule,
    CdkTextareaAutosize],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="te-edit">
      <div class="te-baslik">
        <div class="te-baslik-ikon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
          </svg>
        </div>
        <span>Teklif Bilgileri</span>
      </div>

      <div class="te-form">
        <div class="form-grup">
          <label>Firma / Müşteri</label>
          <div class="input-wrap">
            <nz-select
              [ngModel]="p()?.firma"
              (ngModelChange)="guncelle('firma', $event)"
              nzPlaceHolder="Firma seçin..."
              nzAllowClear nzShowSearch
              style="width:100%"
              [nzCustomTemplate]="firmaTpl">
              @for (f of firmalar(); track f._id) {
                <nz-option [nzValue]="f.firmaAdi" [nzLabel]="f.firmaAdi" nzCustomContent>
                  <div class="sel-item"><span class="sel-emoji">🏭</span><div class="sel-bilgi"><span class="sel-ad">{{ f.firmaAdi }}</span>@if (f.yetkili) { <span class="sel-detay">{{ f.yetkili }}</span> }</div></div>
                </nz-option>
              }
            </nz-select>
            <ng-template #firmaTpl let-selected>
              <span class="sel-item"><span class="sel-emoji">🏭</span><span class="sel-ad">{{ selected.nzLabel }}</span></span>
            </ng-template>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="form-grup">
          <label>Yetkili Kişi</label>
          <div class="input-wrap">
            <input nz-input autocomplete="off" readonly onfocus="this.removeAttribute('readonly')"
              [ngModel]="p()?.yetkiliKisi || ''"
              (ngModelChange)="guncelle('yetkiliKisi', $event)"
              placeholder="Yetkili kişi adı..."/>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="te-ayirici"></div>

        <div class="form-grup">
          <label>Ürün / Hizmet <span class="zorunlu">*</span></label>
          <div class="input-wrap">
            <input nz-input autocomplete="off" readonly onfocus="this.removeAttribute('readonly')"
              [ngModel]="p()?.urunHizmet || ''"
              (ngModelChange)="guncelle('urunHizmet', $event)"
              placeholder="Ürün veya hizmet adı..."/>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="form-satir">
          <div class="form-grup flex-1">
            <label>Miktar</label>
            <div class="input-wrap">
              <nz-input-number
                [ngModel]="p()?.miktar || 1"
                (ngModelChange)="guncelle('miktar', $event)"
                [nzMin]="1" [nzStep]="1"
                style="width:100%">
              </nz-input-number>
              <div class="input-glow"></div>
            </div>
          </div>
          <div class="form-grup flex-1">
            <label>Birim Fiyat</label>
            <div class="input-wrap">
              <nz-input-number
                [ngModel]="p()?.birimFiyat || 0"
                (ngModelChange)="guncelle('birimFiyat', $event)"
                [nzMin]="0" [nzStep]="0.01"
                style="width:100%">
              </nz-input-number>
              <div class="input-glow"></div>
            </div>
          </div>
        </div>

        <div class="form-grup">
          <label>Para Birimi</label>
          <div class="input-wrap">
            <nz-select
              [ngModel]="p()?.paraBirimi || 'TRY'"
              (ngModelChange)="guncelle('paraBirimi', $event)"
              style="width:100%"
              [nzCustomTemplate]="tekPbTpl">
              <nz-option nzValue="TRY" nzLabel="TRY" nzCustomContent>
                <div class="sel-item"><span class="sel-emoji">₺</span><span class="sel-ad">TRY - Türk Lirası</span></div>
              </nz-option>
              <nz-option nzValue="USD" nzLabel="USD" nzCustomContent>
                <div class="sel-item"><span class="sel-emoji">$</span><span class="sel-ad">USD - Amerikan Doları</span></div>
              </nz-option>
              <nz-option nzValue="EUR" nzLabel="EUR" nzCustomContent>
                <div class="sel-item"><span class="sel-emoji">€</span><span class="sel-ad">EUR - Euro</span></div>
              </nz-option>
            </nz-select>
            <ng-template #tekPbTpl let-selected>
              <span class="sel-item"><span class="sel-emoji">{{ selected?.nzValue === 'TRY' ? '₺' : selected?.nzValue === 'USD' ? '$' : '€' }}</span><span class="sel-ad">{{ selected?.nzLabel }}</span></span>
            </ng-template>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="te-ayirici"></div>

        <div class="form-grup">
          <label>Geçerlilik Tarihi</label>
          <div class="input-wrap">
            <nz-date-picker
              [ngModel]="p()?.gecerlilikTarihi"
              (ngModelChange)="guncelle('gecerlilikTarihi', $event)"
              nzPlaceHolder="Tarih seçin..."
              style="width:100%">
            </nz-date-picker>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="form-grup">
          <label>Durum</label>
          <div class="input-wrap">
            <nz-select
              [ngModel]="p()?.durum || 'taslak'"
              (ngModelChange)="guncelle('durum', $event)"
              style="width:100%"
              [nzCustomTemplate]="tekDurumTpl">
              <nz-option nzValue="taslak" nzLabel="Taslak" nzCustomContent>
                <div class="sel-item"><span class="sel-dot" style="background:#94a3b8"></span><span class="sel-ad">Taslak</span></div>
              </nz-option>
              <nz-option nzValue="gonderildi" nzLabel="Gönderildi" nzCustomContent>
                <div class="sel-item"><span class="sel-dot" style="background:#3b82f6"></span><span class="sel-ad">Gönderildi</span></div>
              </nz-option>
              <nz-option nzValue="kabul" nzLabel="Kabul Edildi" nzCustomContent>
                <div class="sel-item"><span class="sel-dot" style="background:#22c55e"></span><span class="sel-ad">Kabul Edildi</span></div>
              </nz-option>
              <nz-option nzValue="reddedildi" nzLabel="Reddedildi" nzCustomContent>
                <div class="sel-item"><span class="sel-dot" style="background:#ef4444"></span><span class="sel-ad">Reddedildi</span></div>
              </nz-option>
            </nz-select>
            <ng-template #tekDurumTpl let-selected>
              <span class="sel-item"><span class="sel-dot" [style.background]="tekDurumRenk(selected?.nzValue)"></span><span class="sel-ad">{{ selected?.nzLabel }}</span></span>
            </ng-template>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="te-ayirici"></div>

        <div class="form-grup">
          <label>Açıklama</label>
          <div class="input-wrap">
            <textarea nz-input autocomplete="off" readonly onfocus="this.removeAttribute('readonly')"
              [ngModel]="p()?.aciklama || ''"
              (ngModelChange)="guncelle('aciklama', $event)"
              placeholder="Teklif hakkında notlar..."
              cdkTextareaAutosize [cdkAutosizeMinRows]="2" [cdkAutosizeMaxRows]="4">
            </textarea>
            <div class="input-glow"></div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`

    :host { display: block; }

    .te-edit {
      display: flex;
      flex-direction: column;
      gap: 0;
      padding: 0;
    }

    .te-baslik {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 16px;
      margin-bottom: 4px;
      border-radius: 10px;
      background: linear-gradient(135deg, var(--py-arkaplan-soluk, rgba(0,0,0,0.03)), transparent);
    }

    .te-baslik-ikon {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #1e3a5f, #2563eb);
      color: #fff;
      flex-shrink: 0;
      box-shadow: 0 2px 8px rgba(30, 58, 95, 0.35);
      position: relative;
    }

    .te-baslik-ikon::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%);
      background-size: 200% 100%;
      animation: shimmer 3s infinite linear;
      pointer-events: none;
    }

    .te-baslik span {
      font-size: 13px;
      font-weight: 600;
      color: var(--py-metin, #333);
      letter-spacing: 0.3px;
    }

    .te-form {
      display: flex;
      flex-direction: column;
      gap: 14px;
      padding: 4px 2px;
    }

    .te-ayirici {
      height: 1px;
      background: linear-gradient(90deg, transparent, var(--py-kenarlik, #d9d9d9) 20%, var(--py-kenarlik, #d9d9d9) 80%, transparent);
      opacity: 0.4;
      margin: 2px 0;
    }

    .form-satir {
      display: flex;
      gap: 12px;
    }

    .flex-1 { flex: 1; }

    .form-grup {
      display: flex;
      flex-direction: column;
      gap: 5px;
    }

    .form-grup label {
      font-size: 11px;
      font-weight: 500;
      color: var(--py-metin-ucuncul, rgba(0,0,0,0.45));
      text-transform: uppercase;
      letter-spacing: 0.6px;
      padding-left: 2px;
      transition: color 0.2s ease;
    }

    .form-grup:focus-within label {
      color: var(--py-link, #1890ff);
    }

    .zorunlu {
      color: #ff4d4f;
      font-size: 13px;
      line-height: 1;
      vertical-align: middle;
    }

    .input-wrap { position: relative; }

    .input-glow {
      position: absolute;
      bottom: 0;
      left: 50%;
      width: 0;
      height: 2px;
      border-radius: 1px;
      background: linear-gradient(90deg, #1e3a5f, #2563eb, #1e3a5f);
      transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1), left 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
      opacity: 0;
      pointer-events: none;
      z-index: 1;
    }

    .input-wrap:focus-within .input-glow {
      width: 100%;
      left: 0;
      opacity: 1;
    }

    .input-wrap input[nz-input],
    .input-wrap textarea[nz-input] {
      background: var(--py-arkaplan-soluk, rgba(0,0,0,0.02));
      border: 1px solid var(--py-kenarlik, #d9d9d9);
      border-radius: 8px;
      padding: 7px 11px;
      font-size: 13px;
      color: var(--py-metin, #333);
      transition: border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
    }

    .input-wrap input[nz-input]:hover,
    .input-wrap textarea[nz-input]:hover {
      border-color: var(--py-kenarlik-hover, #b3b3b3);
      background: var(--py-arkaplan-hover, #f5f5f5);
    }

    .input-wrap input[nz-input]:focus,
    .input-wrap textarea[nz-input]:focus {
      border-color: var(--py-kenarlik-aktif, #1890ff);
      background: var(--py-input-arka, #fff);
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
      outline: none;
    }

    .input-wrap ::ng-deep .ant-select-selector {
      background: var(--py-arkaplan-soluk, rgba(0,0,0,0.02)) !important;
      border: 1px solid var(--py-kenarlik, #d9d9d9) !important;
      border-radius: 8px !important;
      min-height: 34px;
      transition: border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
    }

    .input-wrap:hover ::ng-deep .ant-select-selector {
      border-color: var(--py-kenarlik-hover, #b3b3b3) !important;
      background: var(--py-arkaplan-hover, #f5f5f5) !important;
    }

    .input-wrap:focus-within ::ng-deep .ant-select-selector {
      border-color: var(--py-kenarlik-aktif, #1890ff) !important;
      background: var(--py-input-arka, #fff) !important;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1) !important;
    }

    .input-wrap ::ng-deep nz-date-picker {
      width: 100%;
    }

    .input-wrap ::ng-deep .ant-picker {
      background: var(--py-arkaplan-hover, rgba(0,0,0,.02)) !important;
      border: 1px solid var(--py-kenarlik, rgba(0,0,0,.1)) !important;
      border-radius: 8px !important;
      min-height: 34px;
      width: 100% !important;
      font-size: 13px !important;
      transition: border-color .25s, box-shadow .25s, background .25s !important;
    }
    .input-wrap ::ng-deep .ant-picker input { font-size: 13px !important; color: var(--py-metin) !important; }

    .input-wrap:hover ::ng-deep .ant-picker {
      border-color: rgba(102,126,234,.3) !important;
      background: var(--py-arkaplan-hover, rgba(0,0,0,.01)) !important;
    }

    .input-wrap:focus-within ::ng-deep .ant-picker,
    .input-wrap ::ng-deep .ant-picker-focused {
      border-color: rgba(102,126,234,.5) !important;
      background: transparent !important;
      box-shadow: 0 0 0 2px rgba(102,126,234,.08) !important;
    }

    .input-wrap ::ng-deep nz-input-number {
      width: 100%;
    }

    .input-wrap ::ng-deep .ant-input-number {
      background: var(--py-arkaplan-soluk, rgba(0,0,0,0.02));
      border: 1px solid var(--py-kenarlik, #d9d9d9);
      border-radius: 8px;
      transition: border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
    }

    .input-wrap:hover ::ng-deep .ant-input-number {
      border-color: var(--py-kenarlik-hover, #b3b3b3);
      background: var(--py-arkaplan-hover, #f5f5f5);
    }

    .input-wrap:focus-within ::ng-deep .ant-input-number {
      border-color: var(--py-kenarlik-aktif, #1890ff);
      background: var(--py-input-arka, #fff);
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
    }

    @keyframes shimmer {
      0% { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
  `],
})
export class TeklifEditComponent extends FikoYTKokBilesenComponent {
  private lookup = inject(HmiLookupService);
  firmalar = signal<(ILookupMusteri | ILookupTedarikci)[]>([]);
  parametreler = model<any>();

  constructor() { super(); this.lookup.firmalariYukle().then(l => this.firmalar.set(l)); }

  p = computed(() => this.parametreler()?.ek || {});

  tekDurumRenk(v: string): string {
    const m: Record<string, string> = { 'taslak': '#94a3b8', 'gonderildi': '#3b82f6', 'kabul': '#22c55e', 'reddedildi': '#ef4444' };
    return m[v] || '#94a3b8';
  }

  guncelle(alan: string, deger: any) {
    const params = this.parametreler();
    if (params?.ek) {
      params.ek[alan] = deger;
      this.parametreler.set({ ...params });
    }
  }
}
